<template>
  <div
    v-if="info"
    :class="[brandClass, { 'is-dark': isDark }]"
    class="MaintenancePage"
  >
    <div class="MaintenancePage__inner">
      <div class="logo">
        <img
          :src="logo"
          :alt="brandName"
          height="40"
        >
      </div>

      <main
        class="card"
        role="status"
        aria-labelledby="maintenance-title"
      >
        <div
          class="icon"
          aria-hidden="true"
        >
          <svg
            viewBox="0 0 40 40"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
          >
            <circle
              cx="20"
              cy="20"
              r="5.5"
            />
            <circle
              cx="20"
              cy="20"
              r="11"
            />
            <circle
              cx="20"
              cy="20"
              r="14.5"
              stroke-width="4"
              stroke-dasharray="4.2 7.19"
              stroke-linecap="butt"
            />
          </svg>
        </div>

        <h1 id="maintenance-title">
          {{ title }}
        </h1>
        <p class="lead">
          {{ lead }}
        </p>

        <div
          v-if="hasTime"
          class="time"
          aria-label="維護時間"
        >
          <div class="cell">
            <span class="label">維護開始</span>
            <time :datetime="info.datetimeStart">
              <span class="clock">{{ startClock }}</span>
              <br>
              <span class="date">{{ startDate }}</span>
            </time>
          </div>
          <span
            class="arrow"
            aria-hidden="true"
          >→</span>
          <div class="cell">
            <span class="label">{{ endLabel }}</span>
            <time :datetime="info.datetimeEnd">
              <span class="clock">{{ endClock }}</span>
              <br>
              <span class="date">{{ endDate }}</span>
            </time>
          </div>
          <span class="tz">台灣時間（UTC+8）</span>
        </div>

        <div
          v-if="countdown"
          class="live"
          aria-live="polite"
          aria-atomic="true"
        >
          <p class="countdown">
            {{ countdownLabel }}
            <b>{{ countdown.hours }}</b> 小時
            <b>{{ countdown.minutes }}</b> 分
          </p>
        </div>

        <div class="safe">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <path d="M12 2 4 5v6c0 5 3.4 9.4 8 11 4.6-1.6 8-6 8-11V5l-8-3z" />
            <path d="m9 12 2 2 4-4" />
          </svg>
          <p>本次為計畫性升級，您的帳戶與訂單資料不會因維護而變更，恢復後即可照常使用。</p>
        </div>

        <div class="actions">
          <button
            type="button"
            class="btn btn-primary"
            @click="reload"
          >
            重新整理頁面
          </button>
          <div class="actions actions--line">
            <a
              :href="lineLink"
              class="btn btn-line"
              target="_blank"
              rel="noopener"
              @click="onLineClick"
            >
              <span
                class="dot"
                aria-hidden="true"
              ><i /></span>
              有急事？用 LINE 聯絡客服
            </a>
            <p class="line-note">
              維護期間回覆可能較慢，我們會在恢復後盡快處理。{{ hint }}
            </p>
          </div>
        </div>
      </main>

      <p class="footer">
        感謝您的耐心等候。— {{ brandName }} 團隊
      </p>
    </div>
  </div>
</template>

<script>
import { isMagicbuyProject, getProjectBrandName } from '@g/utils/projectUtils'
import { isInApp, actOpenSysBrowser } from '@g/utils/appSDK'
import {
  MAINTENANCE_PHASE,
  getMaintenancePhase,
  formatMaintenanceClock,
  formatMaintenanceDate,
  getMaintenanceCountdown,
} from '@g/utils/maintenance'
import logoMgb from '@g/assets/images/maintenance-logo-mgb.png'
import logoKkren from '@g/assets/images/maintenance-logo-kkren.png'

// 倒计时最多每 30 秒刷新一次，画面只显示到分钟
const TICK_INTERVAL = 30 * 1000

const LINE_LINKS = {
  mgb: 'https://line.me/ti/p/@magicbuy',
  kkren: 'https://line.me/ti/p/@kkren',
}

// 全屏系统维护页：读取根 store 的 maintenance，非空即覆盖整个应用
export default {
  name: 'MaintenancePage',
  data () {
    return {
      now: Date.now(),
      timer: null,
      prevTitle: '',
      prevBodyOverflow: '',
    }
  },
  computed: {
    info () {
      return this.$store.state.maintenance
    },
    isMgb () {
      return isMagicbuyProject(this.$store.state.project)
    },
    brandClass () {
      return this.isMgb ? 'is-mgb' : 'is-kkren'
    },
    brandName () {
      return getProjectBrandName(this.$store.state.project)
    },
    logo () {
      return this.isMgb ? logoMgb : logoKkren
    },
    lineLink () {
      return this.isMgb ? LINE_LINKS.mgb : LINE_LINKS.kkren
    },
    isDark () {
      // H5 为 Vuetify 2（$vuetify.theme.dark），PC 为 Vuetify 1（$vuetify.dark）
      const vuetify = this.$vuetify || {}
      if (vuetify.theme && typeof vuetify.theme.dark === 'boolean') {
        return vuetify.theme.dark
      }
      return Boolean(vuetify.dark)
    },
    phase () {
      return getMaintenancePhase(this.info, this.now)
    },
    isOver () {
      return this.phase === MAINTENANCE_PHASE.over
    },
    title () {
      if (this.isOver) return '維護時間較原訂延後'
      if (this.phase === MAINTENANCE_PHASE.before) return '系統升級維護預告'
      return '系統升級維護中'
    },
    lead () {
      if (this.isOver) return '維護仍在進行中，我們正加緊完成。請稍後再重新整理。'
      if (this.info.content) return this.info.content
      if (this.phase === MAINTENANCE_PHASE.before) {
        return `${this.brandName}將於下列時段進行計畫性系統升級，期間網站與 App 暫停使用。`
      }
      return `${this.brandName}正在進行計畫性系統升級，網站與 App 暫停使用。`
    },
    hint () {
      return this.isOver ? '恢復服務後，重新整理即可繼續使用。' : '本頁不會自動更新，恢復後請重新整理。'
    },
    hasTime () {
      return Boolean(this.info.datetimeStart || this.info.datetimeEnd)
    },
    startClock () {
      return formatMaintenanceClock(this.info.datetimeStart)
    },
    startDate () {
      return formatMaintenanceDate(this.info.datetimeStart)
    },
    endClock () {
      return formatMaintenanceClock(this.info.datetimeEnd)
    },
    endDate () {
      return formatMaintenanceDate(this.info.datetimeEnd)
    },
    endLabel () {
      return this.isOver ? '原預計恢復' : '預計恢復'
    },
    countdownLabel () {
      return this.phase === MAINTENANCE_PHASE.before ? '距離開始約' : '距離預計恢復約'
    },
    countdown () {
      if (this.isOver) return null
      const target = this.phase === MAINTENANCE_PHASE.before ? this.info.datetimeStart : this.info.datetimeEnd
      return getMaintenanceCountdown(target, this.now)
    },
    documentTitle () {
      return this.info ? `${this.title}｜${this.brandName}` : ''
    },
  },
  watch: {
    info: {
      handler (info) {
        if (info) {
          this.activate()
        } else {
          this.deactivate()
        }
      },
      immediate: true,
    },
    documentTitle (title) {
      if (title) document.title = title
    },
  },
  beforeDestroy () {
    this.deactivate()
  },
  methods: {
    activate () {
      if (this.timer) return
      this.now = Date.now()
      this.prevTitle = document.title
      this.prevBodyOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'
      document.title = this.documentTitle
      this.timer = setInterval(this.tick, TICK_INTERVAL)
      document.addEventListener('visibilitychange', this.onVisibilityChange)
    },
    deactivate () {
      if (!this.timer) return
      clearInterval(this.timer)
      this.timer = null
      document.removeEventListener('visibilitychange', this.onVisibilityChange)
      document.body.style.overflow = this.prevBodyOverflow
      document.title = this.prevTitle
    },
    tick () {
      this.now = Date.now()
    },
    onVisibilityChange () {
      if (!document.hidden) this.tick()
    },
    reload () {
      window.location.reload()
    },
    onLineClick (event) {
      // App 内需交给原生打开系统浏览器，否则会在 WebView 内打开 LINE 网页
      if (isInApp()) {
        event.preventDefault()
        actOpenSysBrowser(this.lineLink)
      }
    },
  },
}
</script>

<style lang="postcss" scoped>
.MaintenancePage {
  /* 中性色（浅色） */
  --bg: #F5F6F8;
  --card: #FFFFFF;
  --text: #1B1F26;
  --muted: #5B6470;
  --line: #E3E6EB;
  --panel: #F5F6F8;
  --safe: #176B3C;
  --line-brand: #06C755;
  --line-border: #C9CED6;

  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 100000000;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  background: var(--bg);
  color: var(--text);
  font-family: "PingFang TC", "Noto Sans TC", "Microsoft JhengHei", system-ui, -apple-system, sans-serif;
  font-size: 16px;
  line-height: 1.6;
  -webkit-font-smoothing: antialiased;
  text-align: center;
}

/* 買即可：蓝 */
.MaintenancePage.is-mgb {
  --brand: #0A6FB8;
  --brand-btn: #0A6FB8;
  --brand-fg: #FFFFFF;
  --brand-soft: #E3F2FC;
}
.MaintenancePage.is-mgb.is-dark {
  --brand: #5FBDF5;
  --brand-btn: #5FBDF5;
  --brand-fg: #0B1A26;
  --brand-soft: #122A3B;
}

/* 巧巧郎：黄 */
.MaintenancePage.is-kkren {
  --brand: #9A6300;
  --brand-btn: #F0B90F;
  --brand-fg: #2B1F04;
  --brand-soft: #FDF3D6;
}
.MaintenancePage.is-kkren.is-dark {
  --brand: #F2C13A;
  --brand-btn: #F2C13A;
  --brand-fg: #1F1704;
  --brand-soft: #332A10;
}

.MaintenancePage.is-dark {
  --bg: #0F1720;
  --card: #171F2B;
  --text: #ECEFF3;
  --muted: #A3ACB8;
  --line: #2A3441;
  --panel: #111927;
  --safe: #7FD1A0;
  --line-border: #3A4553;
}

.MaintenancePage__inner {
  min-height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: calc(24px + env(safe-area-inset-top)) calc(20px + env(safe-area-inset-right)) calc(24px + env(safe-area-inset-bottom)) calc(20px + env(safe-area-inset-left));
}

.logo {
  margin-bottom: 20px;
  display: flex;
  justify-content: center;
}
.logo img {
  height: 40px;
  width: auto;
  display: block;
}

.card {
  width: 100%;
  max-width: 560px;
  background: var(--card);
  border: 1px solid var(--line);
  border-radius: 16px;
  padding: 36px 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 20px;
}

.icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: var(--brand-soft);
  display: grid;
  place-items: center;
  color: var(--brand);
}
.icon svg {
  width: 32px;
  height: 32px;
  animation: MaintenancePage-spin 14s linear infinite;
}
@keyframes MaintenancePage-spin {
  to { transform: rotate(360deg); }
}

h1 {
  margin: 0;
  font-size: 26px;
  line-height: 1.4;
  font-weight: 700;
  letter-spacing: .01em;
  color: var(--text);
}
.lead {
  margin: 0;
  font-size: 16px;
  color: var(--muted);
  max-width: 26em;
}

.time {
  width: 100%;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  background: var(--panel);
  border-radius: 12px;
  padding: 16px 12px;
  gap: 8px;
}
.time .cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.time .label {
  font-size: 13px;
  color: var(--muted);
}
.time .clock {
  font-size: 22px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  letter-spacing: .01em;
}
.time .date {
  font-size: 14px;
  color: var(--muted);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.time .arrow {
  color: var(--muted);
  font-size: 14px;
}
.time .tz {
  grid-column: 1 / -1;
  font-size: 12px;
  color: var(--muted);
  margin-top: 2px;
  text-align: center;
}

.live {
  width: 100%;
}
.countdown {
  margin: 0;
  font-size: 15px;
  color: var(--muted);
  font-variant-numeric: tabular-nums;
}
.countdown b {
  font-size: 22px;
  font-weight: 700;
  color: var(--brand);
  padding: 0 2px;
}

.safe {
  width: 100%;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  text-align: left;
  border-left: 3px solid var(--safe);
  padding: 4px 0 4px 14px;
  font-size: 14px;
  color: var(--text);
}
.safe svg {
  flex: none;
  width: 20px;
  height: 20px;
  margin-top: 2px;
  color: var(--safe);
}
.safe p {
  margin: 0;
}

.actions {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.actions--line {
  gap: 8px;
}

.btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 48px;
  width: 100%;
  border-radius: 10px;
  font: inherit;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  border: 1.5px solid transparent;
  background: none;
  padding: 0 16px;
}
.btn:focus-visible {
  outline: 2px solid var(--brand);
  outline-offset: 3px;
}
.btn-primary {
  background: var(--brand-btn);
  color: var(--brand-fg);
}
.btn-line {
  background: var(--card);
  color: var(--text);
  border-color: var(--line-border);
}
.btn-line .dot {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: var(--line-brand);
  display: grid;
  place-items: center;
}
.btn-line .dot i {
  display: block;
  width: 8px;
  height: 6px;
  border-radius: 2px;
  background: #FFF;
}
.line-note {
  margin: -4px 0 0;
  font-size: 13px;
  color: var(--muted);
}

.footer {
  margin: 20px 0 0;
  font-size: 13px;
  color: var(--muted);
  text-align: center;
}

@media (max-width: 480px) {
  .logo img { height: 36px; }
  .card { padding: 28px 20px; gap: 18px; }
  .time { padding: 14px 8px; }
}
@media (max-width: 340px) {
  .time .date { font-size: 13px; }
}
@media (prefers-reduced-motion: reduce) {
  .icon svg { animation: none; }
}
</style>
